/**
 * Display names for the enum and code values the API returns as-is (since 2026-10-25). The API keeps
 * sending the stored names, which the filters post back, so only what is SHOWN changes: short,
 * sentence case, words spaced. A value missing from a table still reads well through `humanize`.
 */

/** "VelocityAnomaly" → "Velocity anomaly", "KYC" → "KYC", "Session Created" unchanged. */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  if (/\s/.test(value)) return value;
  const words = value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/);
  // Acronyms (all capitals, 2+ letters) keep their case; other words after the first go lower case.
  return words
    .map((w, i) => (i > 0 && !/^[A-Z0-9]{2,}$/.test(w) ? w.toLowerCase() : w))
    .join(' ');
}

function lookup(table: Record<string, string>, value: string | null | undefined): string {
  if (!value) return '';
  return table[value] ?? humanize(value);
}

/** FraudAlertType (Core). */
export const FRAUD_ALERT_TYPE_LABELS: Record<string, string> = {
  DuplicateDocument: 'Duplicate document',
  SuspiciousPattern: 'Suspicious pattern',
  VelocityAnomaly: 'Repeated use',
  DeviceFingerprint: 'Device fingerprint',
  GeoMismatch: 'Location mismatch',
  DocumentTampering: 'Tampered document',
  FaceSpoof: 'Face spoof',
  BlacklistedEntity: 'Blocklisted',
  SyntheticIdentity: 'Synthetic identity',
  DataInconsistency: 'Data mismatch',
  InvalidDocumentData: 'Invalid document data',
  FaceMismatch: 'Face mismatch',
};

export const fraudAlertTypeLabel = (v: string | null | undefined) => lookup(FRAUD_ALERT_TYPE_LABELS, v);

/** DocumentType (Core), in the order the filters list them. */
export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  DriverLicense: "Driver's license",
  IdentificationCard: 'ID card',
  Passport: 'Passport',
  ResidencePermit: 'Residence permit',
  UtilityBill: 'Utility bill',
  BankStatement: 'Bank statement',
  Check: 'Check',
  Other: 'Other',
};

export const documentTypeLabel = (v: string | null | undefined) => lookup(DOCUMENT_TYPE_LABELS, v);

/** A Bootstrap icon per DocumentType, shown beside its name; anything else gets a plain document. */
const DOCUMENT_TYPE_ICONS: Record<string, string> = {
  DriverLicense: 'bi-car-front-fill',
  IdentificationCard: 'bi-person-vcard',
  Passport: 'bi-passport',
  ResidencePermit: 'bi-house-check',
  UtilityBill: 'bi-lightning-charge',
  BankStatement: 'bi-bank',
  Check: 'bi-cash-stack',
};

export const documentTypeIcon = (v: string | null | undefined) =>
  (v && DOCUMENT_TYPE_ICONS[v]) || 'bi-file-earmark-text';

/** VerificationType (Core). */
export const VERIFICATION_TYPE_LABELS: Record<string, string> = {
  KYC: 'KYC',
  KYB: 'KYB',
  AgeVerification: 'Age verification',
  AddressVerification: 'Address verification',
  // The /v1/documents/* calls (extract and verify); named after the service, not the enum.
  DocumentOnly: 'ID extract',
};

export const verificationTypeLabel = (v: string | null | undefined) => lookup(VERIFICATION_TYPE_LABELS, v);

/** AuditLog.Action: the "Entity.Event" codes Core writes. Older free-text actions pass through. */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  'ApiLog.ViewBody': 'API log body viewed',
  'Client.AgeVerificationStepsUpdated': 'Age verification steps updated',
  'Client.BetaUpdated': 'Beta Live updated',
  'Client.ContactUpdated': 'Contact updated',
  'Client.CustomTermsUpdated': 'Custom rates updated',
  'Client.DecisionRuleChanged': 'Fraud rule changed',
  'Client.KybApproved': 'Live access approved',
  'Client.FaceMatchThresholdChanged': 'Face match threshold changed',
  'Client.KybRejected': 'Live access rejected',
  'Client.KybSubmitted': 'Live access requested',
  'Client.MinimumAgeUpdated': 'Minimum age updated',
  'Client.NotificationPreferencesUpdated': 'Notifications updated',
  'Client.PlanChanged': 'Plan changed',
  'Client.ReturnUrlsUpdated': 'Return URLs updated',
  'Client.StatusChanged': 'Account status changed',
  'Client.ThresholdChanged': 'Decline threshold changed',
  'Client.WebhookSecretRegenerated': 'Webhook secret regenerated',
  'Client.WebhookUrlUpdated': 'Webhook URL updated',
  'FraudAlert.StatusChanged': 'Alert status changed',
  'Settings.DecisionRuleChanged': 'Decision rule changed',
  'Settings.FaceMatchThresholdChanged': 'Face match minimum changed',
  'Settings.TwoFactorRequirementChanged': 'Two-step sign-in requirement changed',
  'TryExtractor.Run': 'Try Extractor run',
  'Verification.ManualReview': 'Decision changed',
};

/** An unknown "Entity.Event" code shows its event part spaced out; the entity has its own column. */
export function auditActionLabel(action: string | null | undefined): string {
  if (!action) return '';
  if (AUDIT_ACTION_LABELS[action]) return AUDIT_ACTION_LABELS[action];
  const dot = action.indexOf('.');
  return humanize(dot > 0 && !/\s/.test(action) ? action.slice(dot + 1) : action);
}

/** AuditLog.EntityType, in the order the filter lists them. */
export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  VerificationSession: 'Verification',
  Client: 'Client',
  FraudAlert: 'Fraud alert',
  ApiRequestLog: 'API log',
  SystemSettings: 'Platform settings',
  User: 'User',
  ApiKey: 'API key',
};

export const auditEntityLabel = (v: string | null | undefined) => lookup(AUDIT_ENTITY_LABELS, v);
